import React, {CSSProperties, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import GridTable, {
  DEFAULT_GRID_TABLE__OPTIONS
} from '@components/system/GridTable/GridTable';
import {
  CUSTOM_PAGINATION_SIZE_OPTIONS,
  PAGINATION_SIZE
} from 'src/constants/SystemConstants';
import {
  DEFAULT_WHITE_COLOR,
  FONT_PRIMARY_COLOR,
  NO_PRIORITY_COLOR,
  PRIORITY_HIGH_COLOR,
  PRIORITY_LOW_COLOR,
  PRIORITY_MEDIUM_COLOR,
  SELECTED_GRID_ROW_BACKGROUND_COLOR,
  TABLE_ODD_ROW_COLOR
} from 'src/style/variable';
import CCMShowFullNotesDialog from '../../ChronicCareManagement/module-components/CCMShowFullNotesDialog/CCMShowFullNotesDialog';
import {Column, Options, Query, QueryResult} from 'material-table';
import {
  ITicket,
  IWorkFlow,
  IWorkFlowStatus
} from '@shared/services/service-mappers/WorkflowMapper';
import {fromHeaderStyles} from '../../Sms/components/IncomingSmsInfoGridTableStyles';
import {UserModelMapper} from '@shared/services/service-mappers/ModelMapper';
import {_patientDrawerDetails} from '@shared/store/actions/PatientDrawerAction';
import {useDispatch, useSelector} from 'react-redux';
import {
  TICKETING_SYSTEM_CUSTOM_FILTER_KEY_MAPPER,
  PatientsTabKeys,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER,
  PriorityOptionsList,
  AppPermissions
  // FILTER_AGE_BY
} from '@shared/constants/Mapping';
import HelperUtils from '@shared/utils/HelperUtils';
import UpdateValueLink from '../../../../../components/system/UpdateValueLink/UpdateValueLink';
import {getAllTickets, updateTicket} from '@shared/services/WorkflowServices';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {CancelTokenSource, axios} from '@shared/axios';
import TicketingSystemTicketDetails from './TicketingSystemTicketDetails/TicketingSystemTicketDetails';
import {ITicketingSystemFilter} from '../TicketingSystemContext/TicketSystemReducer/TicketSystemReducer';
import {getFilteredNextSteps, getWorkflowStatusLabel} from '../helpers/utils';
import {
  IAllPossibleTicketModules,
  IGroupPermission
} from '../hooks/useTicketingSystemPermission';
// eslint-disable-next-line max-len
import {ITicketingSystemTicketDetailsModuleFieldsProps} from './TicketingSystemTicketDetails/TicketingSystemTicketDetailsModuleFields/TicketingSystemTicketDetailsModuleFields';
import {CommunicationService} from 'src/service/CommunicationService';
import {ITicketingSystemTicketActivityItemProps} from './TicketingSystemTicketDetails/TicketingSystemTicketActivity/TicketActivityItem/TicketActivityItem';
import './TicketingSystemGrid.scss';
import {Chip} from '@material-ui/core';
import {State} from '@shared/store/reducers/Index';
import {Permission} from '@shared/services/PermissionService';

interface ITicketingSystemGridProps {
  clinicGroupId: number;
  clinicId: number;
  workflowId: IWorkFlow['id'];
  statuses: IWorkFlowStatus[];
  allClinicUsers: UserModelMapper[];
  patientId?: number;
  ticketFilters?: Record<string, ITicketingSystemFilter>;
  showPatientInfoInGridAndTicketDetails?: boolean;
  module: IAllPossibleTicketModules;
  groupPermission?: IGroupPermission;
  renderTicketDetailsModuleInputFields?: ITicketingSystemTicketDetailsModuleFieldsProps['renderFields'];
  renderTicketActivityItemContent?: ITicketingSystemTicketActivityItemProps['renderTicketActivityItemContent'];
  hidePatientInfo?: boolean;
  userId: number;
  messageEventType?: string;
  onRowSelect?: (row: ITicket[]) => void;
  gridOption?: Options<any>;
  ticketDetailsView: {
    show: boolean;
    ticketId?: number | null;
  };
  updateTicketDetailsView: ({
    show,
    ticketId
  }: {
    show: boolean;
    ticketId?: number | null;
  }) => void;
}

const commonCellStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  padding: '0.7rem'
};

const TicketingSystemGrid = (props: ITicketingSystemGridProps): JSX.Element => {
  const {t} = useTranslation();
  const {
    allClinicUsers,
    clinicGroupId,
    clinicId,
    patientId,
    workflowId,
    statuses,
    ticketFilters,
    showPatientInfoInGridAndTicketDetails = true,
    module,
    groupPermission,
    renderTicketDetailsModuleInputFields,
    renderTicketActivityItemContent,
    userId,
    messageEventType,
    gridOption = {},
    onRowSelect,
    ticketDetailsView,
    updateTicketDetailsView
  } = props;
  const selectedStatusId = ticketFilters?.status?.value;
  const expandedView = !ticketDetailsView.show;

  const dispatch = useDispatch();
  const [
    columnValueMoreDetailsDialog,
    setColumnValueMoreDetailsDialog
  ] = useState({
    show: false,
    notes: '',
    title: ''
  });

  const {enqueueSnackbar} = useSnackbar();
  const tableRef = useRef<any>();

  const resetPagesInGrid = (pageNumber?: number) => {
    if (
      tableRef.current?.onQueryChange &&
      typeof tableRef.current.onQueryChange === 'function'
    ) {
      if (typeof pageNumber === 'number') {
        tableRef.current.onQueryChange({page: pageNumber});
      } else {
        tableRef.current.onQueryChange();
      }
    }
  };

  const updateTicketInfo = async ({
    assignedToUserId,
    ticketId,
    // eslint-disable-next-line no-shadow
    selectedStatusId,
    selectedStatusCode
  }: {
    assignedToUserId?: UserModelMapper['id']; // Optional for 'selectedStatusId'
    ticketId: ITicket['id'];
    selectedStatusId?: IWorkFlowStatus['id']; // Optional for 'assignedToUserId'
    selectedStatusCode?: IWorkFlowStatus['code']; // Optional for 'assignedToUserId'
  }) => {
    try {
      const payload: Parameters<typeof updateTicket>[0]['payload'] = {};
      // Determine payload based on type
      if (assignedToUserId) {
        payload.assigned_to = assignedToUserId;
      }
      if (selectedStatusId && selectedStatusCode) {
        (payload.workflow_status_id = selectedStatusId),
          (payload.workflow_status_code = selectedStatusCode);
      }
      // Check if payload is correctly constructed
      if (Object.keys(payload).length === 0) {
        throw new Error('Invalid payload');
      }
      const res = await updateTicket({
        clinicGroupId,
        clinicId,
        ticketId,
        payload,
        params: {
          module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
        }
      });
      if (selectedStatusId && clinicGroupId && clinicId) {
        CommunicationService.sendEvent({
          action: 'UPDATE_TICKET_STATUS_COUNT',
          data: null
        });
      }
      enqueueSnackbar(res.message, SnackBarConfig('s'));
      resetPagesInGrid();
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };

  useEffect(() => {
    resetPagesInGrid(0);
  }, [ticketFilters, clinicGroupId, clinicId, selectedStatusId, patientId]);

  const openPatientDetailsDrawer = ({
    defaultTab,
    ticketPatientId
  }: {
    ticketPatientId: number;
    defaultTab: string;
  }) => {
    dispatch(
      _patientDrawerDetails(
        clinicGroupId,
        clinicId,
        ticketPatientId,
        defaultTab
      )
    );
  };

  const getPriorityLabelFromId = (id: number) => {
    const priorityItem = PriorityOptionsList.find(item => item.value === id);
    if (priorityItem) return priorityItem.label;
    return '';
  };

  const priorityChipsClasses = ['no-priority', 'low', 'medium', 'high'];
  const clinicsList: any = useSelector(
    (state: State) => state.clinics.list || []
  );
  const selectedClinic = clinicsList.find(
    (clinic: any) => clinic.id === clinicId
  );
  const clinicTicketConfig: Record<string, any> = React.useMemo(() => {
    const ticketConfig = {};
    if (Array.isArray(selectedClinic?.ticket_config)) {
      selectedClinic.ticket_config.forEach(
        (i: {config_key: string; config_value: string}) => {
          ticketConfig[i.config_key] = i.config_value;
        }
      );
    }
    return ticketConfig;
  }, [selectedClinic]);
  const userPermissions = useSelector(
    (state: any) => state.auth.userPermissions
  );
  const ticketColumnDef: Array<Column<ITicket>> = [
    {
      title: t('TICKETING_SYSTEM.TICKET_GRID.HEADERS.TICKETS'),
      sorting: false,
      headerStyle: {
        ...fromHeaderStyles,
        lineHeight: '1.2rem',
        color: FONT_PRIMARY_COLOR,
        width: '100%'
      } as CSSProperties,
      cellStyle: {
        ...commonCellStyle,
        width: '100%'
      } as CSSProperties,
      render: row => {
        return (
          <div className="tickets-column">
            <p className="ticket-title">{row.title}</p>
            {showPatientInfoInGridAndTicketDetails && (
              <p
                className="link-style patient-name-link"
                onClick={event => {
                  event.stopPropagation();
                  openPatientDetailsDrawer({
                    defaultTab:
                      PatientsTabKeys[module as keyof typeof PatientsTabKeys],
                    ticketPatientId: row.patient.id
                  });
                }}>
                {HelperUtils.commaSeparatedFullName(
                  row.patient.first_name,
                  row.patient.last_name,
                  row.patient.middle_name
                )}
              </p>
            )}
            <p>
              {t('TICKETING_SYSTEM.TICKET_GRID.HEADERS.STATUS')}:{' '}
              {getWorkflowStatusLabel(statuses, row.workflow_status_id)}
            </p>
          </div>
        );
      },
      hidden: expandedView
    },
    {
      title: t('TICKETING_SYSTEM.TICKET_GRID.HEADERS.PATIENT'),
      sorting: true,
      headerStyle: {
        ...fromHeaderStyles,
        lineHeight: '1.2rem',
        color: FONT_PRIMARY_COLOR,
        width: '15%'
      } as CSSProperties,
      cellStyle: {
        ...commonCellStyle,
        width: '15%'
      } as CSSProperties,
      hidden: !showPatientInfoInGridAndTicketDetails || !expandedView,
      render: row => (
        <p
          className="link-style patient-name-column"
          onClick={event => {
            event.stopPropagation();
            openPatientDetailsDrawer({
              defaultTab:
                PatientsTabKeys[module as keyof typeof PatientsTabKeys],
              ticketPatientId: row.patient.id
            });
          }}>
          {HelperUtils.commaSeparatedFullName(
            row.patient.first_name,
            row.patient.last_name,
            row.patient.middle_name
          )}
        </p>
      )
    },
    {
      title: t('TICKETING_SYSTEM.TICKET_GRID.HEADERS.TITLE'),
      sorting: true,
      headerStyle: {
        ...fromHeaderStyles,
        lineHeight: '1.2rem',
        color: FONT_PRIMARY_COLOR,
        width: '20%'
      } as CSSProperties,
      cellStyle: {
        ...commonCellStyle,
        width: '20%'
      } as CSSProperties,
      render: row => {
        return (
          <div className="title-column">
            <p>{row?.title}</p>
            <p
              className={'link-style'}
              onClick={() => {
                updateTicketDetailsView({show: true, ticketId: row.id});
              }}>
              {t('TICKETING_SYSTEM.VIEW')}
            </p>
          </div>
        );
      },
      hidden: !expandedView
    },
    {
      title: t('TICKETING_SYSTEM.TICKET_GRID.HEADERS.PRIORITY'),
      sorting: true,
      headerStyle: {
        ...fromHeaderStyles,
        lineHeight: '1.2rem',
        color: FONT_PRIMARY_COLOR,
        width: '10%'
      } as CSSProperties,
      cellStyle: {
        ...commonCellStyle,
        width: '10%'
      } as CSSProperties,
      render: row => {
        const priorityLabel = getPriorityLabelFromId(row.priority);
        return (
          <p className="priority-column">
            {priorityLabel ? (
              <Chip
                className={`${
                  priorityChipsClasses[row.priority] ?? ''
                } priority-chip`}
                label={priorityLabel}
                variant="outlined"
              />
            ) : (
              '-'
            )}
          </p>
        );
      },
      hidden: !expandedView
    },
    ...(module === 'PRIOR_AUTHORIZATION'
      ? [
          {
            title: t('TICKETING_SYSTEM.TICKET_GRID.HEADERS.DATE_OF_SERVICE'),
            sorting: true,
            headerStyle: {
              ...fromHeaderStyles,
              lineHeight: '1.2rem',
              color: FONT_PRIMARY_COLOR,
              width: '10%'
            } as CSSProperties,
            cellStyle: {
              ...commonCellStyle,
              width: '10%'
            } as CSSProperties,
            render: row => {
              let dateOfService = '';
              if (row.prior_auth?.date_of_service) {
                dateOfService = HelperUtils.formatDisplayDate(
                  row.prior_auth?.date_of_service
                );
              }
              return (
                <p className="date-of-service-column">{dateOfService || '-'}</p>
              );
            },
            hidden: !expandedView
          }
        ]
      : []),
    {
      title: t('TICKETING_SYSTEM.TICKET_GRID.HEADERS.LAST_WORKED_DATE'),
      sorting: true,
      headerStyle: {
        ...fromHeaderStyles,
        lineHeight: '1.2rem',
        color: FONT_PRIMARY_COLOR,
        width: '15%'
      } as CSSProperties,
      cellStyle: {
        ...commonCellStyle,
        width: '15%'
      } as CSSProperties,
      render: row => {
        let lastWorkedOnDate = '';
        if (row.last_worked_at) {
          lastWorkedOnDate = HelperUtils.formatDisplayDateTime(
            row.last_worked_at
          );
        }
        return (
          <p className="last-worked-date-column">{lastWorkedOnDate || '-'}</p>
        );
      },
      hidden: !expandedView
    },
    {
      title: t('TICKETING_SYSTEM.TICKET_GRID.HEADERS.STATUS'),
      sorting: false,
      headerStyle: {
        ...fromHeaderStyles,
        lineHeight: '1.2rem',
        color: FONT_PRIMARY_COLOR,
        width: '15%'
      } as CSSProperties,
      cellStyle: {
        ...commonCellStyle,
        width: '15%'
      } as CSSProperties,
      hidden: typeof selectedStatusId === 'number' || !expandedView,
      render: row => {
        let allStatusOfSteps: IWorkFlowStatus[] = [];

        if (row?.workflow_status_id) {
          const currentStatus = statuses.find(
            i => i.id === row.workflow_status_id
          );
          if (currentStatus) {
            allStatusOfSteps = [currentStatus];
          }
          if (
            currentStatus?.nextSteps &&
            Array.isArray(currentStatus?.nextSteps) &&
            currentStatus.nextSteps.length > 0
          ) {
            const filteredNextSteps = getFilteredNextSteps(
              currentStatus,
              clinicTicketConfig,
              TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
            );
            allStatusOfSteps = [...allStatusOfSteps, ...filteredNextSteps];
          }
        }
        if (row.is_completed) {
          return getWorkflowStatusLabel(statuses, row?.workflow_status_id);
        }
        const hasReviewCodesPermission = Permission.hasGenerateCodesOrReviewCodesPermissions(
          userPermissions,
          AppPermissions.PRIOR_AUTHORIZATION.REVIEW_CODES,
          clinicGroupId
        ) as any;
        const hasGenerateCodesPermission = Permission.hasGenerateCodesOrReviewCodesPermissions(
          userPermissions,
          AppPermissions.PRIOR_AUTHORIZATION.GENERATE_CODES,
          clinicGroupId
        ) as any;
        const isStatusGenerateCode =
          getWorkflowStatusLabel(statuses, row.workflow_status_id) ===
          'Generate Code';
        const isStatusReviewCode =
          getWorkflowStatusLabel(statuses, row.workflow_status_id) ===
          'Review Codes';
        return (
          <div className="assigned-to-column" key={row.id}>
            {(!isStatusGenerateCode || hasGenerateCodesPermission) &&
            (!isStatusReviewCode || hasReviewCodesPermission) ? (
              <UpdateValueLink
                value={row.workflow_status_id}
                optionsList={allStatusOfSteps.map(status => ({
                  label: status.label,
                  value: status.id
                }))}
                onSelectOption={(selectedStatus, event) => {
                  if (event) {
                    event.stopPropagation();
                  }
                  const status = allStatusOfSteps.find(
                    s => s.id === selectedStatus.id
                  );
                  updateTicketInfo({
                    selectedStatusId: selectedStatus.id,
                    ticketId: row.id,
                    selectedStatusCode: status.code
                  });
                }}
              />
            ) : (
              <div className="statuses-in-grid">
                <p className="status">
                  {getWorkflowStatusLabel(statuses, row.workflow_status_id)}
                </p>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: t('TICKETING_SYSTEM.TICKET_GRID.HEADERS.ASSIGNED_TO'),
      sorting: true,
      headerStyle: {
        ...fromHeaderStyles,
        lineHeight: '1.2rem',
        color: FONT_PRIMARY_COLOR,
        width: '20%'
      } as CSSProperties,
      cellStyle: {
        ...commonCellStyle,
        width: '20%'
      } as CSSProperties,
      render: row => {
        return (
          <div className="assigned-to-column" key={row.id}>
            <UpdateValueLink
              value={row.assigned_user?.id}
              optionsList={allClinicUsers.map(clinicUser => ({
                label: HelperUtils.formFullName(
                  clinicUser.first_name,
                  clinicUser.last_name
                ),
                value: clinicUser.id
              }))}
              onSelectOption={(selectedUser, event) => {
                if (event) {
                  event.stopPropagation();
                }
                updateTicketInfo({
                  assignedToUserId: selectedUser.id,
                  ticketId: row.id
                });
              }}
              addValueLabel={t('TICKETING_SYSTEM.ASSIGN_TO')}
            />
            <p>
              {row.assigned_user?.assigned_at &&
                HelperUtils.formatDisplayDateTime(
                  row.assigned_user.assigned_at
                )}
            </p>
          </div>
        );
      },
      hidden: !expandedView
    }
  ];

  const getAllTicketsAPIReq = React.useRef<CancelTokenSource | null>(null);
  const fetchAllTickets: (
    query?: Query<ITicket>
  ) => Promise<QueryResult<ITicket>> = async query => {
    // RESET ROW SELECTION
    if (onRowSelect) {
      onRowSelect([]);
    }
    if (getAllTicketsAPIReq.current) {
      // Cancelling previous API calls
      getAllTicketsAPIReq.current.cancel();
    }
    getAllTicketsAPIReq.current = axios.CancelToken.source();
    const params: Parameters<typeof getAllTickets>[0]['params'] = {
      module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
    };
    params.workflow_id = workflowId;
    // use the filters instead
    if (selectedStatusId) {
      if (
        Object.values(TICKETING_SYSTEM_CUSTOM_FILTER_KEY_MAPPER).includes(
          selectedStatusId as any
        )
      ) {
        if (
          selectedStatusId ===
          TICKETING_SYSTEM_CUSTOM_FILTER_KEY_MAPPER.ALL_PRIOR_AUTHORIZATIONS
        ) {
          params.is_open_tickets = true;
        } else if (
          selectedStatusId ===
          TICKETING_SYSTEM_CUSTOM_FILTER_KEY_MAPPER.MY_PRIOR_AUTHORIZATIONS
        ) {
          params.is_open_tickets = true;
          params.assigned_to = userId;
        } else if (
          selectedStatusId ===
          TICKETING_SYSTEM_CUSTOM_FILTER_KEY_MAPPER.UNASSIGNED_PRIOR_AUTHORIZATIONS
        ) {
          params.is_open_tickets = true;

          params.is_unassigned_tickets = true;
        } else if (
          selectedStatusId ===
          TICKETING_SYSTEM_CUSTOM_FILTER_KEY_MAPPER.UN_ATTENDED_TICKETS
        ) {
          params.is_open_tickets = true;
          params.is_unattended_ticket = true;
        } else if (
          selectedStatusId ===
          TICKETING_SYSTEM_CUSTOM_FILTER_KEY_MAPPER.RESOLVED_TICKETS
        ) {
          params.resolved_ticket = true;
        } else if (
          selectedStatusId ===
          TICKETING_SYSTEM_CUSTOM_FILTER_KEY_MAPPER.NEEDS_CLINIC_ATTENTION
        ) {
          params.is_need_clinic_attention = true;
        } else if (
          selectedStatusId ===
          TICKETING_SYSTEM_CUSTOM_FILTER_KEY_MAPPER.CLOSED_TICKETS
        ) {
          params.is_open_tickets = false;
        }
      }
      // **TODO: refactor this with status later */
      else if (
        selectedStatusId &&
        typeof selectedStatusId === 'string' &&
        selectedStatusId.startsWith('filter_by')
      ) {
        const customFilter = new URLSearchParams(selectedStatusId);
        customFilter.forEach((value, key) => {
          params[key] = value;
          params.is_open_tickets = true;
        });
      } else {
        params.workflow_status_id = selectedStatusId;
        if (module === 'PRIOR_AUTHORIZATION') {
          params.is_open_tickets = true;
        }
      }
    }
    if (patientId) {
      params.patient_id = patientId;
    }
    if (ticketFilters?.patient?.value) {
      params.patient_name = ticketFilters?.patient?.value;
    }
    // TODO: need to refactor the filters here completely
    for (const filterKey of Object.keys(ticketFilters ?? {})) {
      if (['status', 'patient'].includes(filterKey)) continue;
      // ** below is the dashboard filters and filter chips related code **/
      if (
        (ticketFilters?.[filterKey] as any)?.type === 'query_params' &&
        ticketFilters?.[filterKey]?.value
      ) {
        const customFilter = new URLSearchParams(
          ticketFilters?.[filterKey]?.value
        );
        customFilter.forEach((value, key) => {
          params[key] = value;
        });
      } else if (
        ticketFilters?.[filterKey]?.value !== undefined &&
        ticketFilters?.[filterKey]?.value !== null &&
        ticketFilters?.[filterKey]?.value !== ''
      ) {
        params[filterKey] = ticketFilters?.[filterKey]?.value;
      }
    }

    if (query) {
      params.limit = query?.pageSize || PAGINATION_SIZE.TICKETING_SYSTEM;
      params.offset = query.page * query.pageSize;
      params.sort_by =
        typeof query?.orderBy?.title === 'string'
          ? query?.orderBy?.title
          : null;
      params.order =
        typeof query?.orderDirection === 'string'
          ? query?.orderDirection
          : null;
    } else {
      params.limit = PAGINATION_SIZE.TICKETING_SYSTEM;
      params.offset = 0;
      params.sort_by = null;
      params.order = null;
    }

    const res = await getAllTickets({
      clinicGroupId,
      clinicId,
      params,
      cancelToken: getAllTicketsAPIReq.current
    });
    if (res.data?.rows) {
      const maxPageNumber =
        Math.max(Number(res.data.count) - 1, 0) /
        PAGINATION_SIZE.TICKETING_SYSTEM;
      const pageNumber = Math.floor(Math.min(query?.page ?? 0, maxPageNumber));

      // IF SOME ITEMS ARE REMOVED IN THAT CASE QUERY PAGE NUMBER WILL BE GREATER THAN THE MAX PAGE NUMBER
      // MAKE ANOTHER REQUEST TO GET THE CORRECT DATA
      if (maxPageNumber && query?.page && query?.page > maxPageNumber) {
        // UPDATE THE OFF SET
        params.offset = pageNumber * query.pageSize;
        const allTicketsRes2 = await getAllTickets({
          clinicGroupId,
          clinicId,
          params,
          cancelToken: getAllTicketsAPIReq.current
        });
        if (allTicketsRes2.data.rows) {
          return {
            data: allTicketsRes2.data.rows,
            page: pageNumber ?? 0,
            totalCount: Number(res.data.count)
          };
        }
        return {
          data: [],
          page: pageNumber ?? 0,
          totalCount: 0
        };
      }
      return {
        data: res.data.rows,
        page: query?.page ?? 0,
        totalCount: Number(res.data.count)
      };
    }
    return {
      data: [],
      page: query?.page ?? 0,
      totalCount: 0
    };
  };

  /** This useEffect comes into picture when the refresh icon is triggered */
  useEffect(() => {
    const subscription = CommunicationService.getEvent().subscribe(event => {
      if (event.action === 'REFRESH_TICKETING_GRID') {
        resetPagesInGrid(event.data?.pageNumber);
      }
    });
    const unsubscribe = () => {
      subscription.unsubscribe();
    };
    return unsubscribe;
  }, []);

  return (
    <div
      className={`ticketing-grid-wrapper ${
        ticketDetailsView.show ? 'show-ticket-details' : ''
      }`}>
      <div className="ticketing-system-grid">
        <GridTable
          tableRef={tableRef}
          onSelectionChange={onRowSelect}
          options={{
            ...DEFAULT_GRID_TABLE__OPTIONS,
            paging: true,
            sorting: true,
            emptyRowsWhenPaging: false,
            pageSize: PAGINATION_SIZE.TICKETING_SYSTEM,
            pageSizeOptions: CUSTOM_PAGINATION_SIZE_OPTIONS.TICKETING_SYSTEM,
            showEmptyDataSourceMessage: true,
            maxBodyHeight: '100%',
            rowStyle: (rowData: any, index) => {
              const rowStyle: CSSProperties = {};
              rowStyle.fontSize = '0.875rem';
              if (index % 2) {
                rowStyle.backgroundColor = DEFAULT_WHITE_COLOR;
              } else {
                rowStyle.backgroundColor = TABLE_ODD_ROW_COLOR;
              }
              if (
                ticketDetailsView.ticketId &&
                rowData.id === ticketDetailsView.ticketId
              ) {
                rowStyle.backgroundColor = SELECTED_GRID_ROW_BACKGROUND_COLOR;
              }
              rowStyle.borderLeftWidth = 4;
              rowStyle.borderLeftColor = NO_PRIORITY_COLOR;
              rowStyle.borderLeftStyle = `solid`;
              if (rowData.priority === 1) {
                rowStyle.borderLeftColor = PRIORITY_LOW_COLOR;
              } else if (rowData.priority === 2) {
                rowStyle.borderLeftColor = PRIORITY_MEDIUM_COLOR;
              } else if (rowData.priority === 3) {
                rowStyle.borderLeftColor = PRIORITY_HIGH_COLOR;
              }
              return rowStyle;
            },
            ...gridOption
          }}
          localization={{
            body: {
              emptyDataSourceMessage: t('TICKETING_SYSTEM.NO_RECORDS')
            }
          }}
          onRowClick={(event: any, row: ITicket) => {
            updateTicketDetailsView({show: true, ticketId: row.id});
          }}
          columns={ticketColumnDef}
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            boxShadow:
              '0 0 0 1px rgb(63 63 68 / 5%), 0 1px 3px 0 rgb(63 63 68 / 15%)',
            cursor: 'pointer',
            maxHeight: '100%',
            height: '100%',
            justifyContent: 'space-between'
          }}
          data={fetchAllTickets}
          onChangePage={() => {
            updateTicketDetailsView({show: false, ticketId: null});
          }}
        />
        {/** Renders the more details modal when the characters length exceeds*/}
        <CCMShowFullNotesDialog
          open={columnValueMoreDetailsDialog.show}
          onClose={() => {
            setColumnValueMoreDetailsDialog(prevState => ({
              ...prevState,
              show: false,
              notes: '',
              title: ''
            }));
          }}
          notes={columnValueMoreDetailsDialog.notes}
          title={columnValueMoreDetailsDialog.title}
        />
      </div>
      {ticketDetailsView.show && (
        <TicketingSystemTicketDetails
          messageEventType={messageEventType}
          key={`${ticketDetailsView.ticketId}-${clinicId}`}
          clinicGroupId={clinicGroupId}
          clinicId={clinicId}
          workflowId={workflowId}
          statusesList={statuses}
          // TODO:
          // onCloseTicketDetails={closeTicketDetailsView}
          onCloseTicketDetails={() => {
            updateTicketDetailsView({show: false, ticketId: null});
          }}
          ticketId={ticketDetailsView.ticketId}
          // TODO: why do I need to reset pages in grid on submit
          onSubmit={resetPagesInGrid}
          // TODO: UPDATE IT
          hidePatientName={!showPatientInfoInGridAndTicketDetails}
          refreshTicketListGrid={resetPagesInGrid}
          showTicketDetails={(ticketId: number) => {
            updateTicketDetailsView({show: true, ticketId});
          }}
          module={module}
          groupPermission={groupPermission}
          renderModuleInputFields={renderTicketDetailsModuleInputFields}
          renderTicketActivityItemContent={renderTicketActivityItemContent}
          clinicTicketConfig={clinicTicketConfig}
        />
      )}
    </div>
  );
};

export default TicketingSystemGrid;
